import React, { useEffect, useState } from 'react';

import { useParams, useSearchParams } from 'react-router-dom';
import { Box, styled, themeGet } from '@qwealth/qcore';
import { ClientComplianceReview, ComplianceQuestion, YesNoNaOption } from 'data/models/Compliance';
import CompletionCheckbox from '../Common/CompletionCheckbox';
import ComplianceButton from './ComplianceButton';
import { useAppSelector } from 'data/store';
import { selectClientComplianceReview } from 'data/selectors/compliance';
import { BsStarFill } from 'react-icons/bs';
import { isShortCodeAutoChecked } from 'utils/ComplianceUtil';

const StyledQuestionLink = styled.button`
  border: none;
  background: none;
  text-align: left;
  font-size: ${themeGet('fontSizes.default')};

  &:hover {
    font-weight: ${themeGet('fontWeights.bold')};
  }
`;

interface SectionQuestionProps {
  question: ComplianceQuestion;
  isCompliant: boolean | undefined;
  isAdvisorView?: boolean;
  selected?: YesNoNaOption;
  incrementNonCompliance: (change: 1 | -1) => void;
  updateQuestionAnswer: (questionId: string, option: string) => void;
  updateParentQuestion?: (prevOption: YesNoNaOption, option: YesNoNaOption) => void;
}

const SectionQuestion: React.FC<SectionQuestionProps> = ({
  question,
  isCompliant,
  isAdvisorView,
  updateParentQuestion,
  updateQuestionAnswer,
  incrementNonCompliance,
}) => {
  const selectedClient: ClientComplianceReview | undefined = useAppSelector(selectClientComplianceReview);
  const selectedContactQID = useParams().contactQID;
  const [searchParams, setSearchParams] = useSearchParams();
  const isAutoChecked = isShortCodeAutoChecked(question.shortCode);

  useEffect(() => {
    if (question.value !== null) {
      clickSelection(question.value as YesNoNaOption);
    }
  }, [question]);

  const [optionSelected, setOptionSelected] = useState<YesNoNaOption>(isCompliant ? 'yes' : undefined);

  const clickQuestionLink = () => {
    searchParams.set('section', question.shortCode);
    setSearchParams(searchParams);
  };

  const clickSelection = (option: YesNoNaOption) => {
    if (option === optionSelected) {
      return;
    }

    updateQuestionAnswer(question.id.toString(), option as string);
    const prevOption = optionSelected;
    if (prevOption === 'no') {
      incrementNonCompliance(-1);
    } else if (option === 'no') {
      incrementNonCompliance(1);
    }

    option === optionSelected ? setOptionSelected(undefined) : setOptionSelected(option);

    if (updateParentQuestion) {
      updateParentQuestion(prevOption, option);
    }
  };

  return (
    <Box display="flex" flexDirection="row" gap="default" alignItems={updateParentQuestion ? '' : 'center'}>
      {isAdvisorView ? (
        <CompletionCheckbox isCompliant={isCompliant} />
      ) : (
        <Box display="flex" flexDirection="row" gap="default">
          <ComplianceButton type="yes" selected={optionSelected === 'yes'} clickOption={() => clickSelection('yes')} />
          <ComplianceButton type="no" selected={optionSelected === 'no'} clickOption={() => clickSelection('no')} />
          {question?.type === 'yes_no_na' && (
            <ComplianceButton type="n/a" selected={optionSelected === 'n/a'} clickOption={() => clickSelection('n/a')} />
          )}
        </Box>
      )}
      <Box display="flex" justifyContent="center" alignItems="center" minWidth="14px">
        {isAutoChecked && <BsStarFill size={14} />}
      </Box>
      <StyledQuestionLink onClick={clickQuestionLink}>{question?.text}</StyledQuestionLink>
    </Box>
  );
};

export default SectionQuestion;
